import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import EmailAPI, {
  MAGIC_LINK_PURPOSES,
  MagicLinkInvalidError,
} from '../../agents/emailAgent';
import { clearSession } from '../../agents/authAgent';
import styles from './MagicLinkLanding.module.css';

/**
 * MagicLinkLanding — destination for the deep link emailed by
 * `POST /api/auth/magic-link/request`. Reads `token` + `purpose` from the
 * URL, verifies via `EmailAPI.verifyMagicLink`, and either:
 *
 *   - Success → caches the returned user (matches authAgent's storage shape)
 *     and bounces the user to `/` (or the redirect path encoded in the URL).
 *   - Invalid / expired → renders a friendly message plus an inline form to
 *     request a fresh link without leaving the page.
 *   - Network / server error → renders a retry affordance that re-runs verify.
 *
 * Accessibility
 * -------------
 * - The full surface is wrapped in `role="status"` + `aria-live="polite"`
 *   so screen readers announce each state transition.
 * - On success and error the primary affordance receives focus so keyboard
 *   users land on the next actionable element without a manual Tab.
 * - The spinner respects `prefers-reduced-motion` via the CSS module.
 *
 * Routing
 * -------
 * The cutthe6.com frontend does not use React Router today; App.js inspects
 * `window.location.pathname` and renders this component when the path equals
 * `/auth/magic-link`. After a successful verify we use `window.location.replace`
 * so the post-login URL is clean and the back button does not return to the
 * (now-consumed) token URL.
 *
 * @param {object} props
 * @param {string} [props.redirectPath] - Optional override for the post-login
 *   landing path. Falls back to URL param `redirect`, then to `/`.
 */
const USER_KEY = 'cutthe6.com_auth_user';

const SUPPORTED_PURPOSES = new Set(Object.values(MAGIC_LINK_PURPOSES));

// Whitelist of in-app redirect paths. We never honor an off-host redirect from
// the URL — open redirects are an auth-flow phishing vector. Anything that
// doesn't start with `/` (or starts with `//` which would resolve to an
// absolute URL in some parsers) falls back to home.
const safeRedirect = (raw) => {
  if (typeof raw !== 'string') return '/';
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/';
  return raw;
};

const parseParams = () => {
  if (typeof window === 'undefined') return { token: '', purpose: 'login', redirect: '/' };
  const params = new URLSearchParams(window.location.search);
  const purposeRaw = params.get('purpose') || MAGIC_LINK_PURPOSES.LOGIN;
  const purpose = SUPPORTED_PURPOSES.has(purposeRaw) ? purposeRaw : MAGIC_LINK_PURPOSES.LOGIN;
  return {
    token: params.get('token') || '',
    purpose,
    redirect: safeRedirect(params.get('redirect') || params.get('redirect_to') || '/'),
  };
};

const MagicLinkLanding = ({ redirectPath }) => {
  const { token, purpose, redirect } = useMemo(parseParams, []);
  const resolvedRedirect = safeRedirect(redirectPath || redirect);

  // Lifecycle states: 'verifying' | 'success' | 'invalid' | 'failed'
  const [status, setStatus] = useState(token ? 'verifying' : 'invalid');
  const [errorMessage, setErrorMessage] = useState(
    token ? '' : 'This sign-in link is missing its token.',
  );

  // Inline "request a new link" form state.
  const [email, setEmail] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [requestError, setRequestError] = useState('');

  // Focus targets for accessibility post-state-change.
  const successHeadingRef = useRef(null);
  const errorActionRef = useRef(null);

  const runVerify = useCallback(async () => {
    setStatus('verifying');
    setErrorMessage('');
    try {
      const result = await EmailAPI.verifyMagicLink({ token, purpose });

      // Cache the user the way authAgent expects so the next render of <App />
      // picks up the session immediately. The httpOnly session cookie is set
      // server-side; we just hydrate the optimistic UI cache.
      if (result?.user) {
        try {
          localStorage.setItem(USER_KEY, JSON.stringify(result.user));
          if (result.user.id != null) {
            localStorage.setItem('cutthe6.com_user_id', String(result.user.id));
          }
        } catch {
          // Storage may be unavailable (private mode) — non-fatal; the cookie
          // is still set and /me will rehydrate on next mount.
        }
      }

      setStatus('success');
      // Give the success banner a beat to render + announce, then redirect.
      setTimeout(() => {
        window.location.replace(resolvedRedirect);
      }, 900);
    } catch (err) {
      if (err instanceof MagicLinkInvalidError) {
        setStatus('invalid');
        setErrorMessage(err.message);
      } else {
        setStatus('failed');
        setErrorMessage(
          err?.message || 'We could not reach the server. Please try again in a moment.',
        );
      }
    }
  }, [token, purpose, resolvedRedirect]);

  // Kick off verification on mount (only if we actually have a token).
  useEffect(() => {
    if (!token) return;
    // Defensive: clear any stale cached user so we don't briefly render the
    // previous session before the new one is established.
    clearSession();
    runVerify();
  }, [token, runVerify]);

  // Move focus when state settles so keyboard / AT users land on the next step.
  useEffect(() => {
    if (status === 'success' && successHeadingRef.current) {
      successHeadingRef.current.focus();
    } else if ((status === 'invalid' || status === 'failed') && errorActionRef.current) {
      errorActionRef.current.focus();
    }
  }, [status]);

  const handleRequestNewLink = async (event) => {
    event.preventDefault();
    setRequestError('');
    if (!email.trim()) {
      setRequestError('Please enter your email address.');
      return;
    }
    setRequestSubmitting(true);
    try {
      await EmailAPI.requestMagicLink({
        email: email.trim(),
        purpose,
        redirectTo: resolvedRedirect,
      });
      setRequestSent(true);
    } catch (err) {
      setRequestError(
        err?.message || 'We could not send the email right now. Please try again.',
      );
    } finally {
      setRequestSubmitting(false);
    }
  };

  // ─── Render helpers ────────────────────────────────────────────────────

  const renderVerifying = () => (
    <div className={styles.spinnerWrap}>
      <div className={styles.spinner} aria-hidden="true" />
      <p className={styles.subtitle} style={{ margin: 0 }}>
        Verifying your sign-in link…
      </p>
    </div>
  );

  const renderSuccess = () => (
    <>
      <div
        className={styles.success}
        role="status"
        ref={successHeadingRef}
        tabIndex={-1}
      >
        <span className={styles.successIcon} aria-hidden="true">✓</span>
        Signed in. Redirecting you now…
      </div>
      <p className={styles.subtitle} style={{ margin: 0 }}>
        If this page doesn't redirect automatically,&nbsp;
        <a href={resolvedRedirect}>continue here</a>.
      </p>
    </>
  );

  const renderRequestForm = () => {
    if (requestSent) {
      return (
        <div className={styles.success} role="status">
          If an account exists for that address, a fresh sign-in link is on its way.
        </div>
      );
    }
    return (
      <form className={styles.form} onSubmit={handleRequestNewLink} noValidate>
        <div className={styles.field}>
          <label htmlFor="magic-link-email" className={styles.label}>
            Email
          </label>
          <input
            id="magic-link-email"
            type="email"
            className={styles.input}
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            disabled={requestSubmitting}
            ref={errorActionRef}
          />
        </div>

        {requestError && (
          <div className={styles.error} role="alert">
            {requestError}
          </div>
        )}

        <button
          type="submit"
          className={styles.primary}
          disabled={requestSubmitting}
        >
          {requestSubmitting ? 'Sending…' : 'Send me a new link'}
        </button>
      </form>
    );
  };

  const renderInvalid = () => (
    <>
      <div className={styles.error} role="alert">
        {errorMessage}
      </div>
      <p className={styles.subtitle}>
        Magic links expire quickly for your security. Enter your email and we'll
        send a fresh one.
      </p>
      {renderRequestForm()}
      <button
        type="button"
        className={styles.linkBtn}
        onClick={() => window.location.assign('/')}
      >
        ← Back to sign in
      </button>
    </>
  );

  const renderFailed = () => (
    <>
      <div className={styles.error} role="alert">
        {errorMessage}
      </div>
      <button
        type="button"
        className={styles.primary}
        onClick={runVerify}
        ref={errorActionRef}
      >
        Try again
      </button>
      <button
        type="button"
        className={styles.linkBtn}
        onClick={() => window.location.assign('/')}
      >
        ← Back to sign in
      </button>
    </>
  );

  const titleByStatus = {
    verifying: 'Signing you in…',
    success: 'You’re signed in',
    invalid: 'Link no longer valid',
    failed: 'Something went wrong',
  };

  return (
    <div className={styles.page}>
      <div
        className={styles.card}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <h1 className={styles.title}>{titleByStatus[status]}</h1>

        {status === 'verifying' && renderVerifying()}
        {status === 'success' && renderSuccess()}
        {status === 'invalid' && renderInvalid()}
        {status === 'failed' && renderFailed()}

        {/* Off-screen narration backup for legacy screen readers that don't
            re-announce when role="status" content swaps wholesale. */}
        <span className={styles.srOnly}>
          Current state: {status}.
        </span>
      </div>
    </div>
  );
};

export default MagicLinkLanding;
